import { useMutation, useQueryClient } from "@tanstack/react-query";

import { featuresKeys } from "@/application/query-keys/features.keys";
import {
  featuresRepository,
  type CreateFeatureInput,
} from "@/infrastructure/repositories/features.supabase.repository";
import { featureRoadmapVerdictsRepository } from "@/infrastructure/repositories/feature-roadmap-verdicts.supabase.repository";
import type { FeatureRow } from "@/infrastructure/supabase/database.types";

export interface UseCreateFeatureVariables {
  input: CreateFeatureInput;
  /**
   * If true, also seed `(all-features, mvp)` for the new feature so it shows
   * up in the MVP column on the All Features board (i.e. it becomes an MVP
   * candidate immediately). Use this when the feature was created from the
   * MVP board.
   */
  seedAsMvp?: boolean;
}

export function useCreateFeature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ input, seedAsMvp }: UseCreateFeatureVariables): Promise<FeatureRow> => {
      const feature = await featuresRepository.create(input);
      if (seedAsMvp) {
        await featureRoadmapVerdictsRepository.upsert({
          featureId: feature.id,
          branch: "all-features",
          verdict: "mvp",
        });
      }
      return feature;
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: featuresKeys.all });
      void qc.invalidateQueries({ queryKey: ["feature-roadmap-verdicts"] });
    },
  });
}
